import { EventEmitter, inject, Injectable } from '@angular/core';
import { io } from 'socket.io-client';
import { AuthenticationService } from '../authentication/authentication.service';
import { Observable } from 'rxjs';
import { PAY_SESSION_SERVICE } from '../api/api.service';
import { environment } from '../../../environments/environment';
import { PaySessionUpdate } from '../../models/PaySessionUpdate';

@Injectable({
	providedIn: 'root',
})
export class PaySessionSocketService {
	private authenticationService = inject(AuthenticationService);

	private socket = io(environment.baseUrl, {
		path: `${PAY_SESSION_SERVICE}socket.io/`,
	});
	private _onReconnect: EventEmitter<void> = new EventEmitter<void>();
	readonly onReconnect = this._onReconnect.asObservable();

	private _onDisconnect: EventEmitter<void> = new EventEmitter<void>();
	readonly onDisconnect = this._onDisconnect.asObservable();

	private _onReconnecting: EventEmitter<boolean> = new EventEmitter<boolean>();
	readonly onReconnecting = this._onReconnecting.asObservable();

	constructor() {
		this.authenticationService.onTokenChange.subscribe(token => {
			this.socket.auth = {
				authorization: token,
			};
			if (this.socket.connected) {
				console.log('Reconnecting to socket.io server with new auth');
				this.socket.disconnect();
				this.socket.connect();
				this._onReconnecting.next(false);
			}
		});
		this.socket.io.on('reconnect_attempt', attempt => {
			console.log('reconnect_attempt', attempt);
			this._onReconnecting.next(true);
		});
		this.socket.io.on('reconnect', () => {
			console.log('reconnected');
			this._onReconnect.next();
			this._onReconnecting.next(false);
		});
		this.socket.on('disconnect', () => {
			console.log('Disconnect');
			this._onReconnecting.next(true);
		});
	}

	async connect(): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			if (this.socket.connected) return resolve();

			const onConnect = () => {
				this._onReconnecting.next(false);
				cleanup();
				console.log('Connected to socket.io server');
				resolve();
			};

			const onError = (e: any) => {
				cleanup();
				console.error(e);
				reject(e);
			};

			const onTimeout = (e: any) => {
				cleanup();
				console.log(e);
				reject(e); // or ignore, depending on your logic
			};

			const cleanup = () => {
				this.socket.off('connect', onConnect);
				this.socket.off('connect_error', onError);
				this.socket.off('connect_timeout', onTimeout);
			};

			this.socket.on('connect', onConnect);
			this.socket.on('connect_error', onError);
			this.socket.on('connect_timeout', onTimeout);
			this.socket.connect();
		});
	}

	paySessionUpdate(): Observable<PaySessionUpdate> {
		return new Observable<PaySessionUpdate>(subscriber => {
			const handler = (data: PaySessionUpdate) => {
				subscriber.next(data);
			};
			this.socket.on('paysession.updated', handler);
			return () => {
				this.socket.off('paysession.updated', handler);
			};
		});
	}
}
